import { ReactNode } from 'react';

import { DateTools, TTableColumn, TTableRow } from '@treezor/design-system';

import {
  filterAttributestransaltionsKeys,
  getAttributeKeys,
  getFormattedAmount,
  useOperationLabelTranslations,
} from '@/features/wallets/components/WalletOperations/operationsHelpers';
import { IAttributeConfig } from '@/features/wallets/store/models/wallet.model';
import { initialFlows, operationStatus, operationtypes } from '@/store/models/operation.model';
import { TWalletOperation, TWalletOperationKeys } from '@/features/wallets/store/models/walletOperation.model';
import { WalletColumnHeader } from '@/features/wallets/components/WalletOperations/WalletColumnHeader';

import { useI18nLocale } from '@/hooks/useI18nLocale';

import { StatusLabel } from '@/ui-kit/status-label/StatusLabel';

import { DashboardHelpers } from '@/utils/DashboardHelpers';

import { getStatusByLabel } from '@/types/colors.types';
import { useAppDispatch } from '@/hooks/redux-helpers';
import { IOperationDetailModalProps } from '@/features/wallets/components/WalletOperations/detail/OperationDetailModal';
import { openModal } from '@/store/actions/core.actions';
import { getProofOfPayout } from '@/features/wallets/store/actions/wallet.actions';
import { WalletOperationActions } from '@/features/wallets/components/WalletOperations/WalletOperationActions';

type TRow = Record<TWalletOperationKeys | 'actions', ReactNode>;

export function useWalletsOperationsData(
  headers: IAttributeConfig[] = [],
  operations: TWalletOperation[] = [],
  userId: number,
) {
  const locale = useI18nLocale();
  const dispatch = useAppDispatch();

  const operationLabelTranslations = useOperationLabelTranslations(
    getAttributeKeys(headers),
    filterAttributestransaltionsKeys,
  );

  const toggleWalletOperationModal = (operation: TWalletOperation) => {
    dispatch(openModal<IOperationDetailModalProps>('walletOperationDetailModal', { operation }));
  };

  const downloadProofOfPayout = (operation: TWalletOperation) => {
    dispatch(getProofOfPayout(operation.objectId, operation.walletId, userId));
  };

  const columns: TTableColumn<TRow>[] = [...headers, { name: 'actions' }].map((header: IAttributeConfig) => {
    const isAlignRight = ['amount'].includes(header.name);
    return {
      field: filterAttributestransaltionsKeys(header.name) as keyof TRow,
      name: header.name as keyof TRow,
      label: (
        <div className={`flex items-center ${!!isAlignRight ? 'flex-end' : 'flex-start'}`}>
          {header.name !== 'actions' && (
            <WalletColumnHeader attribute={header} name={operationLabelTranslations[header.name]}></WalletColumnHeader>
          )}
        </div>
      ),
    };
  });

  const rows: TTableRow<TRow>[] = operations.map((operation: TWalletOperation) => {
    const defaults = Object.keys(operation).reduce<Record<keyof typeof operation, ReactNode>>((prev, current) => {
      return {
        ...prev,
        [current]: <span>{operation[current] || '-'}</span>,
      };
    }, {} as any);

    const canDownloadProofOfPayout =
      operation.initialFlow === initialFlows.PAYOUT &&
      (operation.operationType === operationtypes.BANK_TRANSFERT ||
        operation.operationType === operationtypes.INSTANT_BANK_TRANSFERT) &&
      (operation.status === operationStatus.AUTHORIZED || operation.status === operationStatus.SETTLED);

    const metasdatas = DashboardHelpers.flattenObject(operation.metadata, 'metadata', (value) => <span>{value}</span>);

    const dates = Object.keys(operation.date).reduce<Record<string, ReactNode>>((prev, current) => {
      return {
        ...prev,
        [`date.${current}`]: (
          <div className="whitespace-nowrap">{DateTools.format(operation.date[current], 'date-time') || ' - '}</div>
        ),
      };
    }, {});

    return {
      ...defaults,
      ...metasdatas,
      ...dates,
      ['metadata.refundDate']: (
        <div className="whitespace-nowrap">
          {DateTools.format(operation.metadata['refundDate'] || '', 'date-time') || ' - '}
        </div>
      ),
      ['metadata.authorizationNote']: (
        <div className="whitespace-nowrap w-13 overflow-hidden text-ellipsis">
          {operation.metadata['authorizationNote'] || '' || '-'}
        </div>
      ),
      amount: (
        <div className={`flex items-center 'flex-end'}`}>
          {getFormattedAmount(
            {
              ...operation.amount,
              amount: operation.amount.amount / 100,
              direction: operation.direction,
            },
            locale,
            operation.status === operationStatus.DECLINED || operation.status === operationStatus.CANCELED,
          )}
        </div>
      ),
      status: (
        <StatusLabel
          label={DashboardHelpers.getStatusLabelTranslation(operation.status)}
          status={getStatusByLabel(operation.status)}
        />
      ),
      actions: (
        <div key={operation.objectId}>
          {canDownloadProofOfPayout ? (
            <WalletOperationActions
              userId={userId}
              downloadProofOfPayout={downloadProofOfPayout}
              operation={operation}
            />
          ) : (
            <></>
          )}
        </div>
      ),
      onClick: () => toggleWalletOperationModal(operation),
    };
  });

  return { columns, rows };
}
